/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { memo, useCallback, useEffect, useState } from "react";
import { DataTableRowData } from "../layout/DataTable";
import { CellEditorBoolean } from "./CellEditorBoolean";
import { useDataTableContext } from "../store/DataTableProvider";
import { CellEditorFormula } from "./CellEditorFormula";
import { CellEditorIdentifier } from "./CellEditorIdentifier";
import { CellEditorText } from "./CellEditorText";
import { CellEditorChoice } from "./CellEditorChoice";
import { CellEditorRoiFrame } from "./CellEditorRoiFrame";
import { CustomCellEditorProps } from "ag-grid-react";
import { DataTableColumnDefinition } from "../store/DataTableProvider.types";
import { CellValue } from "@inscopix/ideas-hyperformula";
import { CellEditorCellStatus } from "./CellEditorCellStatus";
import { ModalEditRowIdentifierFormat } from "../modals/ModalEditRowIdentifierFormat";
import { CellValueRendererBase } from "../cell-value-renderers/CellValueRendererBase";
import { CellEditorFile } from "./CellEditorFile/CellEditorFile";
import { CellEditorNumber } from "./CellEditorNumber";

const styles = {
  root: css`
    align-items: center;
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: flex-start;
    padding: 0px 11px;
    outline: 1px solid #07c;
    outline-offset: -1px;
  `,
};

type CellEditorGenericProps = {
  tableId: string;
  tableKind: "data" | "analysis";
  columnId: string;
  columnName: string;
  columnDefinition: DataTableColumnDefinition;
  isIdColumn: boolean;
  rowId: string;
  formula: string;
  value: CellValue;
} & Pick<
  CustomCellEditorProps<DataTableRowData>,
  "api" | "onValueChange" | "stopEditing" | "eventKey"
>;

/** Base cell editor that wraps all column-type specific cell editors */
const CellEditorBase = ({
  api,
  tableId,
  tableKind,
  columnId,
  columnName,
  columnDefinition,
  isIdColumn,
  rowId,
  stopEditing: stopEditingNative,
  onValueChange,
  formula,
  value,
  eventKey,
}: CellEditorGenericProps) => {
  const isAdvancedMode = useDataTableContext((s) => s.isAdvancedMode);
  const [newFormula, setNewFormula] = useState(formula);

  // Notify the grid of the new formula. If the formula has not changed, set the
  // new value to undefined to prevent a cell edit request event from firing.
  useEffect(() => {
    if (newFormula !== formula) {
      onValueChange(newFormula);
    } else {
      onValueChange(undefined);
    }
  }, [formula, newFormula, onValueChange]);

  /**
   * Stops the editing.
   *
   * The native `stopEditing` function is not used directly because, even when
   * the cancel flag is set, the grid still fires a cell edit request event.
   * @param cancel Pass `true` if you want to cancel the editing (i.e. don't
   * accept changes).
   */
  const stopEditing = useCallback(
    (cancel?: boolean) => {
      if (cancel) {
        onValueChange(undefined);
      }
      stopEditingNative(cancel);
    },
    [onValueChange, stopEditingNative],
  );

  /**
   * Used to disable mousedownCapture to prevent ag grid from closing
   * text cell editors when selecting text
   */
  const isTextEditor = (() => {
    if (isAdvancedMode) {
      return true;
    }
    switch (columnDefinition.kind) {
      case "boolean":
      case "join":
      case "cell_status":
      case "choice":
      case "file":
      case "identifier":
      case "roi_frame":
      case "metadatum":
        return false;
      case "float":
      case "integer":
      case "text":
        return true;
    }
    return false;
  })();

  const editor = (() => {
    if (isIdColumn) {
      return (
        <>
          <ModalEditRowIdentifierFormat onClose={stopEditing} />
          {/* Display cell value in cell when modal is open */}
          <CellValueRendererBase value={value} />
        </>
      );
    }

    if (isAdvancedMode) {
      return (
        <CellEditorFormula
          formula={newFormula}
          onChangeFormula={setNewFormula}
          stopEditing={stopEditing}
        />
      );
    }

    switch (columnDefinition.kind) {
      case "boolean":
        return (
          <CellEditorBoolean
            value={value}
            onChangeFormula={setNewFormula}
            stopEditing={stopEditing}
          />
        );
      case "cell_status":
        return (
          <CellEditorCellStatus
            isReadOnly={false}
            gridApi={api}
            rowId={rowId}
            value={value}
            columnDefinition={columnDefinition}
            onChangeFormula={setNewFormula}
            stopEditing={stopEditing}
          />
        );
      case "choice":
        return (
          <CellEditorChoice
            columnDefinition={columnDefinition}
            value={value}
            onChangeFormula={setNewFormula}
            stopEditing={stopEditing}
          />
        );
      case "file":
        return (
          <CellEditorFile
            tableId={tableId}
            value={value}
            fileTypes={columnDefinition.file_types}
            fileFormats={columnDefinition.file_formats}
            allowMultiSelect={columnDefinition.multiple}
            initialFileFilter={tableKind === "data" ? "unassigned" : "all"}
            onChangeFormula={setNewFormula}
            stopEditing={stopEditing}
          />
        );
      case "float":
        return (
          <CellEditorNumber
            eventKey={eventKey}
            value={value}
            onChangeFormula={setNewFormula}
            stopEditing={stopEditing}
          />
        );
      case "identifier":
        return (
          <CellEditorIdentifier
            columnDefinition={columnDefinition}
            value={value}
            onChangeFormula={setNewFormula}
            stopEditing={stopEditing}
          />
        );
      case "integer":
        return (
          <CellEditorNumber
            eventKey={eventKey}
            value={value}
            onChangeFormula={setNewFormula}
            stopEditing={stopEditing}
          />
        );

      case "roi_frame":
        return (
          <CellEditorRoiFrame
            isReadOnly={false}
            gridApi={api}
            rowId={rowId}
            value={value}
            columnName={columnName}
            columnDefinition={columnDefinition}
            onChangeFormula={setNewFormula}
            stopEditing={stopEditing}
          />
        );
      case "text":
        return (
          <CellEditorText
            eventKey={eventKey}
            value={value}
            onChangeFormula={setNewFormula}
            stopEditing={stopEditing}
          />
        );
      // These column types are not manually editable
      case "join":
      case "metadatum":
        return null;
    }
  })();

  return (
    <span
      css={styles.root}
      onMouseDownCapture={isTextEditor ? (e) => e.stopPropagation() : undefined}
    >
      {editor}
    </span>
  );
};

export const CellEditorBaseMemo = memo(CellEditorBase);
